/**
 * Convert Google Drive sharing URL to direct download URL
 * @param {string} url - Google Drive sharing URL
 * @returns {string} Direct download URL
 */
export const convertGoogleDriveUrl = (url) => {
  // Extract file ID from Google Drive URL
  // Format: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
  const match = url.match(/\/d\/([^/]+)/);
  if (match && match[1]) {
    const fileId = match[1];
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }
  return url; // Return original if not a Google Drive URL
};

/**
 * Fetch file as blob with progress tracking
 * @param {string} url - URL to fetch
 * @param {function} onProgress - Progress callback (0-1)
 * @returns {Promise<Blob>} File blob
 */
export const fetchFileAsBlob = async (url, onProgress = null) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }

  const contentLength = response.headers.get('content-length');
  const total = parseInt(contentLength, 10);

  if (!contentLength || !onProgress) {
    // If no content length or no progress callback, just return the blob
    return await response.blob();
  }

  const reader = response.body.getReader();
  const chunks = [];
  let receivedLength = 0;

  while (true) {
    const { done, value } = await reader.read();

    if (done) break;

    chunks.push(value);
    receivedLength += value.length;

    if (onProgress) {
      onProgress(receivedLength / total);
    }
  }

  // Combine chunks into single blob
  return new Blob(chunks);
};

/**
 * Create File object from blob
 * @param {Blob} blob - File blob
 * @param {string} filename - File name
 * @param {string} mimeType - MIME type
 * @returns {File} File object
 */
export const createFileFromBlob = (blob, filename, mimeType) => {
  return new File([blob], filename, { type: mimeType });
};

/**
 * Download and process a remote media file
 * @param {string} url - URL to download
 * @param {string} filename - File name
 * @param {string} mimeType - MIME type
 * @param {function} onProgress - Progress callback (0-1)
 * @returns {Promise<File>} File object
 */
export const downloadRemoteMedia = async (url, filename, mimeType, onProgress = null) => {
  const directUrl = convertGoogleDriveUrl(url);
  const blob = await fetchFileAsBlob(directUrl, onProgress);
  return createFileFromBlob(blob, filename, mimeType);
};
